import {
  getTradingTeamBoardStatus,
  getTradingTeamBoardTime,
  getTradingTeamDisplayTime
} from '@bo-utils/trading';
import type { ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueInfo } from '@library/types/leagues';
import type { PlayerDescriptors } from '@library/types/player';
import { TradingPlayer } from '@bo-components/trading/TradingPlayer';
import { TradingTeamHeader } from '@bo-components/trading/games/TradingTeamHeader';
import { equals } from 'ramda';
import { getDurationDisplayForLeague } from '@bo-utils/leagues';
import { useState } from 'react';
import { useTradingModalBulkActions } from '@bo-hooks/trading/useTradingModalBulkActions';
import { useTradingTeamBulkActions } from '@bo-hooks/trading/useTradingTeamBulkActions';

export interface TradingTeamProps {
  bulkLeagueIdGroups: Array<Array<string>>;
  changes: Array<Partial<GamePlayerProjectionTrading>>;
  gameStartTime: string;
  gameId: string;
  awayTeam: string;
  handleSelectPlayerModal: ({
    gameStartDateTime,
    newPlayerDescriptors,
    newProjection
  }: {
    gameStartDateTime: string;
    newPlayerDescriptors: PlayerDescriptors;
    newProjection: GamePlayerProjectionTrading;
  }) => void;
  handleShowAutoTradeDrawer: ({
    gameStartDateTime,
    newPlayerDescriptors,
    newProjection
  }: {
    gameStartDateTime: string;
    newPlayerDescriptors: PlayerDescriptors;
    newProjection: GamePlayerProjectionTrading;
  }) => void;
  leagueInfo: LeagueInfo;
  gameIdWithDuration: string;
  players: { [playerId: string]: Array<GamePlayerProjectionTrading> };
  primaryHeader: string;
  secondaryHeader: string;
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'> | false
  ) => void;
  standardStartTimes: Array<string>;
  uniqueStartTimes: Array<string>;
  updateConfirmationModalProp: (
    updateFn: (
      prevProps: Omit<ConfirmationModalProps, 'onCancelModal'>
    ) => Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
  isDisplayTimeVisible?: boolean;
}

export const TradingTeam = ({
  bulkLeagueIdGroups,
  changes,
  gameStartTime,
  gameId,
  awayTeam,
  handleSelectPlayerModal,
  handleShowAutoTradeDrawer,
  leagueInfo,
  gameIdWithDuration,
  players,
  primaryHeader,
  secondaryHeader,
  setConfirmationModalProps,
  updateConfirmationModalProp,
  updateProjection,
  standardStartTimes,
  uniqueStartTimes,
  isDisplayTimeVisible = false
}: TradingTeamProps) => {
  const [showPlayers, setShowPlayers] = useState(true);

  const handleTogglePlayers = () => {
    setShowPlayers(!showPlayers);
  };

  const [sampleTeamProjection] = Object.values(players)[0];
  const { durationId = '', leagueId = '', gameModeIds } = sampleTeamProjection;

  const isBulkTeamInGameEnabled = Object.values(players)
    .flatMap((team) => team)
    .every((player) => player.inGame);

  const { tradingTeamBulkActions } = useTradingTeamBulkActions({
    bulkLeagueIdGroups,
    gameId,
    durationId,
    leagueInfo,
    gameIdWithDuration,
    playerIds: Object.keys(players),
    initialStartTime: uniqueStartTimes[0],
    allStartTimes: uniqueStartTimes,
    awayTeam
  });

  const durationDisplay = getDurationDisplayForLeague({
    durationId,
    leagueId,
    leagueInfo
  });

  const { tradingModalActions: tradingTeamModalActions } =
    useTradingModalBulkActions({
      durationDisplay,
      gameModeIds,
      isBulkGameModesEqual: false,
      setConfirmationModalProps,
      tradingBulkActions: tradingTeamBulkActions,
      updateConfirmationModalProp
    });

  const renderPlayerProjections = () =>
    Object.entries(players).map(([playerId, playerData]) => {
      const [samplePlayerProjection] = playerData;
      const { playerImage, playerName, position } = samplePlayerProjection;

      const isBulkPlayerInGameEnabled = playerData.every(
        (projection) => projection.inGame
      );

      const isBulkPlayerGameModesEqual = playerData
        .map((projection) => projection.gameModeIds)
        .every((otherGameModeIds, _, arr) => equals(otherGameModeIds, arr[0]));

      const playerDescriptors = {
        playerDescription: position ?? '',
        playerImage,
        playerName,
        secondaryHeader
      };

      return (
        <TradingPlayer
          changes={changes}
          durationDisplay={durationDisplay}
          gameStartTime={gameStartTime}
          handleSelectPlayerModal={handleSelectPlayerModal}
          handleShowAutoTradeDrawer={handleShowAutoTradeDrawer}
          isBulkPlayerInGameEnabled={isBulkPlayerInGameEnabled}
          isBulkPlayerGameModesEqual={isBulkPlayerGameModesEqual}
          key={`${gameId}-${durationId}-${leagueId}-${playerId}-projection`}
          leagueId={leagueId}
          playerId={playerId}
          playerDescriptors={playerDescriptors}
          projections={playerData}
          setConfirmationModalProps={setConfirmationModalProps}
          updateConfirmationModalProp={updateConfirmationModalProp}
          updateProjection={updateProjection}
        />
      );
    });

  const firstBoardTime = getTradingTeamBoardTime(players);
  const firstDisplayTime = getTradingTeamDisplayTime(players);

  const boardStatus = getTradingTeamBoardStatus(players);

  return (
    <div className="pt-4 first:pt-0">
      <div className="text-soClean-110 border-soFresh-130 overflow-hidden rounded-md border first-of-type:pt-0">
        <TradingTeamHeader
          boardStatus={boardStatus}
          durationDisplay={durationDisplay}
          firstBoardTime={firstBoardTime}
          firstDisplayTime={firstDisplayTime}
          gameId={gameId}
          handleTogglePlayers={handleTogglePlayers}
          isBulkTeamInGameEnabled={isBulkTeamInGameEnabled}
          key={`${gameId}-${primaryHeader}-header`}
          primaryHeader={primaryHeader}
          secondaryHeader={secondaryHeader}
          setConfirmationModalProps={setConfirmationModalProps}
          showPlayers={showPlayers}
          standardStartTimes={standardStartTimes}
          tradingTeamBulkActions={tradingTeamBulkActions}
          uniqueStartTimes={uniqueStartTimes}
          teamModalActions={tradingTeamModalActions}
          isDisplayTimeVisible={isDisplayTimeVisible}
        />
        {showPlayers && (
          <div className="font-heebo no-scrollbar flex max-h-[505px] flex-col overflow-y-scroll rounded-b-md">
            {renderPlayerProjections()}
          </div>
        )}
      </div>
    </div>
  );
};
